import { useSession, useUser } from "@clerk/clerk-react";
import { Tooltip } from "@heroui/react";
import { Phone, PhoneDisconnect, PhoneCall } from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { Device, Call } from "@twilio/voice-sdk";
import { CallStatusModal } from "./CallStatusModal";

interface TwilioCallButtonProps {
  phoneNumber: string;
  contactName?: string;
  size?: number;
  className?: string;
  onCallStart?: () => void;
  onCallEnd?: () => void;
  onCallCompleted?: (phoneNumber: string, durationSeconds: number) => void;
}

type CallStatus = "idle" | "connecting" | "ringing" | "connected" | "disconnected";

/**
 * TwilioCallButton - Browser-based phone calling using Twilio Voice SDK
 *
 * Requirements:
 * - Backend must have TWILIO_API_KEY, TWILIO_API_SECRET, TWILIO_TWIML_APP_SID set
 * - TwiML App must point to your backend's /voice/twiml/outbound endpoint
 */
export const TwilioCallButton = ({
  phoneNumber,
  contactName,
  size = 14,
  className = "",
  onCallStart,
  onCallEnd,
  onCallCompleted,
}: TwilioCallButtonProps) => {
  const { session } = useSession();
  const { user } = useUser();
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const [_device, setDevice] = useState<Device | null>(null);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [activePhoneNumber, setActivePhoneNumber] = useState<string>("");
  const deviceRef = useRef<Device | null>(null);
  const callStartTimeRef = useRef<number | null>(null);

  // Initialize Twilio Device when component mounts
  const initializeDevice = useCallback(async () => {
    if (!user?.id || deviceRef.current) return;

    try {
      // Get voice token from backend
      const response = await fetchWithAuth(
        `${API_URL}/voice/token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identity: user.id }),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to get voice token");
      }

      const { token } = await response.json();

      // Create new Twilio Device
      const newDevice = new Device(token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        allowIncomingWhileBusy: false,
      });

      // Set up device event handlers
      newDevice.on("registered", () => {
        console.log("Twilio Device registered");
      });

      newDevice.on("error", (error) => {
        console.error("Twilio Device error:", error);
        toast.error(`Telefonie fout: ${error.message}`);
        setCallStatus("idle");
      });

      newDevice.on("tokenWillExpire", async () => {
        // Refresh token before it expires
        try {
          const refreshResponse = await fetchWithAuth(
            `${API_URL}/voice/token`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ identity: user.id }),
            },
            session
          );
          const { token: newToken } = await refreshResponse.json();
          newDevice.updateToken(newToken);
        } catch (error) {
          console.error("Failed to refresh token:", error);
        }
      });

      // Register the device
      await newDevice.register();

      deviceRef.current = newDevice;
      setDevice(newDevice);
    } catch (error) {
      console.error("Failed to initialize Twilio Device:", error);
      // Don't show error toast on init - only show when user tries to call
    }
  }, [user?.id, session]);

  // Clean up device on unmount
  useEffect(() => {
    return () => {
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
    };
  }, []);

  // Format phone number for display
  const formatPhoneDisplay = (phone: string) => {
    return phone.replace(/(\+32)(\d{3})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
  };

  // Make outbound call with specified number
  const makeCall = useCallback(async (numberToCall: string) => {
    if (callStatus !== "idle") return;

    try {
      // Initialize device if not already done
      if (!deviceRef.current) {
        await initializeDevice();
      }

      if (!deviceRef.current) {
        toast.error("Kan telefonie niet initialiseren. Probeer opnieuw.");
        return;
      }

      // Format phone number
      let formattedNumber = numberToCall.replace(/\s+/g, "").replace(/[^0-9+]/g, "");
      if (formattedNumber.startsWith("0")) {
        formattedNumber = "+32" + formattedNumber.substring(1);
      }

      // Show call modal and set active number
      setActivePhoneNumber(formattedNumber);
      setShowCallModal(true);
      setCallStatus("connecting");
      onCallStart?.();

      // Make the call
      const call = await deviceRef.current.connect({
        params: {
          To: formattedNumber,
        },
      });

      setActiveCall(call);

      // Set up call event handlers
      call.on("ringing", () => {
        setCallStatus("ringing");
      });

      call.on("accept", () => {
        setCallStatus("connected");
        callStartTimeRef.current = Date.now();
      });

      call.on("disconnect", () => {
        setCallStatus("disconnected");
        setActiveCall(null);

        // Calculate duration and call onCallCompleted
        if (callStartTimeRef.current && onCallCompleted) {
          const durationSeconds = Math.round((Date.now() - callStartTimeRef.current) / 1000);
          onCallCompleted(phoneNumber, durationSeconds);
        }
        callStartTimeRef.current = null;

        onCallEnd?.();
        // Keep modal open briefly to show "disconnected" state
        setTimeout(() => {
          setCallStatus("idle");
        }, 2000);
      });

      call.on("cancel", () => {
        setCallStatus("idle");
        setActiveCall(null);
        setShowCallModal(false);
        onCallEnd?.();
      });

      call.on("error", (error) => {
        console.error("Call error:", error);
        toast.error(`Gespreksfout: ${error.message}`);
        setCallStatus("idle");
        setActiveCall(null);
        setShowCallModal(false);
        onCallEnd?.();
      });

    } catch (error) {
      console.error("Failed to make call:", error);
      toast.error("Kan niet bellen. Controleer of telefonie is geconfigureerd.");
      setCallStatus("idle");
      setShowCallModal(false);
      onCallEnd?.();
    }
  }, [callStatus, phoneNumber, initializeDevice, onCallStart, onCallEnd, onCallCompleted]);

  // Handle call button click
  const handleCall = useCallback(() => {
    makeCall(phoneNumber);
  }, [phoneNumber, makeCall]);

  // Hang up active call
  const handleHangup = useCallback(() => {
    if (activeCall) {
      activeCall.disconnect();
      setActiveCall(null);
      // Don't set idle immediately - let the disconnect event handle it
    }
  }, [activeCall]);

  // Send DTMF digit during active call
  const handleSendDigit = useCallback((digit: string) => {
    if (activeCall) {
      activeCall.sendDigits(digit);
    }
  }, [activeCall]);

  // Close modal after call ends
  const handleCloseModal = useCallback(() => {
    setShowCallModal(false);
    setActivePhoneNumber("");
  }, []);

  // Get button appearance based on status
  const getButtonAppearance = () => {
    switch (callStatus) {
      case "connecting":
        return {
          icon: <PhoneCall size={size} className="animate-pulse" />,
          className: "text-amber-500 hover:text-amber-600",
          tooltip: "Verbinden...",
        };
      case "ringing":
        return {
          icon: <PhoneCall size={size} className="animate-bounce" />,
          className: "text-amber-500 hover:text-amber-600",
          tooltip: "Gaat over...",
        };
      case "connected":
        return {
          icon: <PhoneDisconnect size={size} />,
          className: "text-red-500 hover:text-red-600",
          tooltip: "Ophangen",
        };
      case "disconnected":
        return {
          icon: <Phone size={size} />,
          className: "text-neutral-400",
          tooltip: "Verbinding verbroken",
        };
      default:
        return {
          icon: <Phone weight="duotone" size={size} />,
          className: "text-neutral-secondary-mid hover:text-moss-primary",
          tooltip: `Bel ${formatPhoneDisplay(phoneNumber)}`,
        };
    }
  };

  const appearance = getButtonAppearance();
  const isInCall = callStatus === "connecting" || callStatus === "ringing" || callStatus === "connected";

  return (
    <>
      <Tooltip content={appearance.tooltip}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isInCall && callStatus === "connected") {
              handleHangup();
            } else if (!isInCall) {
              handleCall();
            }
          }}
          disabled={callStatus === "disconnected"}
          className={`p-0.5 rounded hover:bg-moss-primary/10 transition-colors ${appearance.className} ${className}`}
        >
          {appearance.icon}
        </button>
      </Tooltip>

      {/* Call Status Modal */}
      <CallStatusModal
        isOpen={showCallModal}
        status={callStatus}
        phoneNumber={activePhoneNumber || phoneNumber}
        contactName={contactName}
        onHangup={handleHangup}
        onClose={handleCloseModal}
        onSendDigit={handleSendDigit}
      />
    </>
  );
};

export default TwilioCallButton;
